import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { fadeInAnimation } from 'app/animations';
import { appSettings } from 'app/appSettings';
import { FilterValues } from 'app/core/interfaces/filtervalues.interface';
import { LoadState } from 'app/core/interfaces/generic.interface';
import { FlowSandbox } from 'app/core/sandboxes/flow.sandbox';
import { ScrollService } from 'app/core/services/scroll.service';
import { CDFlorderType, CDTo } from 'app/core/store/contract-details/contract-details.interface';
import { FlowDetail, PackingMaterial } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import * as actions from 'app/core/store/edit-flow-detail/flow-detail.actions';
import { CheckFlowDateResponse, EditFlowDetail } from 'app/core/store/edit-flow-detail/flow-detail.interface';
import { goAction } from 'app/core/store/router/router.actions';
import { SessionState } from 'app/core/store/session/session.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { getFirst$$, util } from 'app/core/util/util';
import { FlorderEditContainer } from 'app/modules/florder/florder-edit-container/florder-edit.container';
import { FlowPanelStatuses } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { distinctUntilChanged, filter, first, map, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-flow-edit',
  templateUrl: './flow-edit.container.html',
  styleUrls: ['../../florder/florder-edit-container/florder-edit.container.scss'],
  animations: [fadeInAnimation],
})
export class FlowEditContainer extends FlorderEditContainer<PackingMaterial> implements OnInit {
  @Output() resetMaxAmountPopup = new EventEmitter();
  triggerPlanningValidation: EventEmitter<boolean> = new EventEmitter();
  flowDetail: FlowDetail;
  customers: Customer[] = null;
  selectedCustomer: LoadState<Customer>;
  customersAllowedToDownload: Customer[] = null;
  isSuperUser: boolean;
  panelStatus: FlowPanelStatuses;

  flowRegistrationTypes: CDFlorderType[];
  flowHandshakeTypes: CDFlorderType[];
  datesRange: {
    startDate: string;
    endDate: string;
  };
  flowDate: CheckFlowDateResponse;
  filterValues: FilterValues;
  maxAmountPopupIsShown: boolean = false;

  isReadOnly = false;
  isCreateFlow = false;
  isEditDispute = false;
  isEditFlow = false;
  isHandshakeFlow = false;
  isLateHandshakeFlow = false;
  sort: { sortField: string, sortAscending: boolean };

  constructor(public sandbox: FlowSandbox, private scrollService: ScrollService) {
    super('flows');
    const types$ = this.store.select(state => ({ flowHandshakeTypes: state.contractInfo.flowHandshakeTypes, flowRegistrationTypes: state.contractInfo.flowRegistrationTypes })).pipe(
      takeWhile(() => !this.destroyed),
      distinctUntilChanged()
    );

    types$.subscribe(types => {
      this.flowRegistrationTypes = types.flowRegistrationTypes;
      this.flowHandshakeTypes = types.flowHandshakeTypes;
    });

    this.store
      .pipe(
        map(state => state.flows.filterValues),
        takeWhile(() => !this.destroyed),
        distinctUntilChanged()
      )
      .subscribe(filterValues => (this.filterValues = filterValues));

    this.store
      .select('editFlowDetail')
      .pipe(
        takeWhile(() => !this.destroyed),
        filter((editFlowDetail: EditFlowDetail) => !!editFlowDetail.flowDetail)
      )
      .subscribe((editFlowDetail: EditFlowDetail) => {
        this.sort = editFlowDetail.sort;
        this.datesRange = editFlowDetail.datesRange;
        this.mode = editFlowDetail.mode;
        this.flowDetail = editFlowDetail.flowDetail;
        this.panelStatus = this.getFlowPanelStatusses(editFlowDetail);

        types$
          .pipe(
            filter(contractInfo => contractInfo.flowRegistrationTypes != null || contractInfo.flowHandshakeTypes != null),
            first()
          )
          .subscribe(() => {
            if (this.flowDetail.etmOrderNumber) {
              this.to = this.findTo(this.flowRegistrationTypes, this.flowDetail);
              if (!this.to) {
                this.to = this.findTo(this.flowHandshakeTypes, this.flowDetail);
              }
            } else {
              const flowType = this.flowRegistrationTypes.find(flowType => flowType.id === this.flowDetail.deliveryMethod.type);
              const from =
                flowType &&
                this.flowDetail.deliveryMethod.from &&
                flowType.shippingConditions.length &&
                flowType.shippingConditions[0].froms.find(from => from.id === this.flowDetail.deliveryMethod.from.id);
              this.to = from && this.flowDetail.deliveryMethod.to && from.tos.find(to => to.id === this.flowDetail.deliveryMethod.to.id);
            }
            this.lineItemValidation = this.to && this.to.lineItemValidation;
            this.incoterm = this.to && this.to.incoterms[0];
          });
        this.flowDate = editFlowDetail.flowDate && editFlowDetail.flowDate.CheckFlowDateResponse;
      });

    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.session),
        distinctUntilChanged()
      )
      .subscribe((session: SessionState) => {
        this.isSuperUser = session.userContext.isSuperUser;
        this.customers = session.customerPermissions.data.createOrUpdateFlow;
        this.selectedCustomer = session.selectedCustomer;
        // To not break any other functionality, customersAllowedToDownload was created
        // when a flow create success popup appears, the download option otherwise uses create or update customers instead of
        // queryOrGet Flows customers
        this.customersAllowedToDownload = session.customerPermissions.data.queryOrGetFlow;
      });

    this.store
      .pipe(
        map(state => state.flows),
        takeWhile(() => !this.destroyed),
        distinctUntilChanged()
      )
      .subscribe(flows => (this.maxAmountPopupIsShown = flows.maxAmountDialogShown));
  }

  private findTo(florderTypes: CDFlorderType[], flow: FlowDetail): CDTo {
    const flowType: CDFlorderType = florderTypes.find(flowType => flowType.id === flow.deliveryMethod.type);
    const from = flowType && flowType.shippingConditions.length && flowType.shippingConditions[0].froms.find(from => from.id === flow.deliveryMethod.from.id);
    return from && from.tos.find(to => to.id === flow.deliveryMethod.to.id);
  }

  private getFlowPanelStatusses(editFlowDetail: EditFlowDetail): FlowPanelStatuses {
    const materialEdit = !!(editFlowDetail.flowDetail && editFlowDetail.flowDetail.materials && editFlowDetail.mode.material);
    const materialExists = materialEdit && editFlowDetail.flowDetail.materials.find(m => m.internalId === editFlowDetail.mode.material.internalId) != null;
    this.defineFlowEditContainerStatus();
    const panelStatusses = this.getPanelStatusses(materialEdit, materialExists);

    return {
      ...panelStatusses,
      delivery: {
        ...panelStatusses.delivery,
        isEdit: panelStatusses.delivery.isEdit && (this.isEditFlow || this.isHandshakeFlow || this.isCreateFlow),
        // isEditable: panelStatusses.delivery.isEditable && (this.isEditFlow || this.isHandshakeFlow || this.isCreateFlow),
        isEditable:
          // delivery is not in edit
          !panelStatusses.delivery.isEdit &&
          // edit-container is open for edit, handshake or create
          (this.isEditFlow || this.isHandshakeFlow || this.isCreateFlow) &&
          // if step 'customer' is open, then you should not be in 'create' state
          (this.mode.editWindow !== 'customer' || this.mode.state !== 'create') &&
          // if isSuperUser, check if customer is in portfolio
          (!this.isSuperUser || !this.flowDetail.customer || this.isCustomerAllowedtoCreateOrUpdate(this.flowDetail.customer.id)),
        isReadonly: {
          flowType: this.isEditFlow || this.isHandshakeFlow || this.isLateHandshakeFlow,
          from: this.isEditFlow || this.isHandshakeFlow || this.isLateHandshakeFlow,
          to: this.isEditFlow || this.isHandshakeFlow || this.isLateHandshakeFlow,
          remarks: this.isHandshakeFlow || this.isLateHandshakeFlow,
          licensePlate: this.isHandshakeFlow || this.isLateHandshakeFlow,
          transporter: this.isHandshakeFlow || this.isLateHandshakeFlow,
          wholesaler: this.isHandshakeFlow || this.isLateHandshakeFlow,
          senderReferenceNumber: false,
          customerReferenceNumber: false,
        },
      },
      detail: {
        ...panelStatusses.detail,
        showNext: panelStatusses.detail.showNext,
        // isEditable: if isCreate, is true if editWindow is after detail
        isEditable:
          // detail is not in edit
          !panelStatusses.detail.isEdit &&
          // edit-container is open for edit, handshake, dispute or create
          (this.isEditFlow || this.isHandshakeFlow || this.isCreateFlow || this.isEditDispute) &&
          // if in create mode, currently window in edit should not be the first (delivery)
          (this.mode.state !== 'create' || !util.objectInArray(this.mode.editWindow, ['delivery', 'customer'])),
        canRemoveExistingLines: !this.flowDetail.handshaker && !this.flowDetail.lateHandshake && !this.isEditDispute,
        showQuantityOriginalAndDefinitive: (!panelStatusses.detail.isEdit && panelStatusses.detail.isView) || this.isEditDispute,
        showQuantityDifference: !panelStatusses.detail.isEdit && panelStatusses.detail.isView && !this.isEditDispute,
        showDisputeQuantities: this.isEditDispute,
        showBack: this.isEditDispute,
        showSaldoColumn: editFlowDetail.showSaldo,
        validateDisputeQuantities: this.isEditDispute,
        limitEditToPackingIds: editFlowDetail.limitEditToPackingIds,
      },
      material: {
        ...panelStatusses.material,
        packingIdEditable: !materialExists,
        disputeQuantityEditable: this.isEditDispute,
        definitiveQuantityEditable: !this.isEditDispute,
        otherFieldsEditable: !this.isLateHandshakeFlow,
        limitedPackingIds: editFlowDetail.limitEditToPackingIds,
      },
      planning: {
        ...panelStatusses.planning,
        isEditable:
          // detail is not in edit
          !panelStatusses.planning.isEdit &&
          // edit-container is open for create
          this.isCreateFlow &&
          // not in create mode (but in create-confirm)
          this.mode.state !== 'create',
      },
    };
  }

  ngOnInit(): void {
    this.scrollService.scrollToElement('top');
  }

  canEdit(): boolean {
    const userCanDo = this.authorization.FLOW.UPDATE;
    const canDo =
      this.mode &&
      this.mode.state === 'view' &&
      this.flowDetail &&
      !this.flowDetail.isLocked &&
      this.flowDetail.status === appSettings.FLOW_STATUS_WAITING_FOR_HANDSHAKE &&
      !this.flowDetail.handshaker &&
      !this.flowDetail.lateHandshake;
    const superUserCanDo = this.isCustomerAllowedtoCreateOrUpdate(this.flowDetail.customer.id);

    return this.isSuperUser ? superUserCanDo && canDo : userCanDo && canDo;
  }

  canHandshake(): boolean {
    return (
      this.mode &&
      this.mode.state === 'view' &&
      this.authorization &&
      this.authorization.FLOW.UPDATE &&
      this.flowDetail &&
      !this.flowDetail.isLocked &&
      this.flowDetail.status === appSettings.FLOW_STATUS_WAITING_FOR_HANDSHAKE &&
      (this.flowDetail.handshaker || this.flowDetail.lateHandshake)
    );
  }

  editFlow() {
    this.store.dispatch(goAction({ path: `/flows/${this.flowDetail.etmOrderNumber}/edit` }));
  }

  handshakeFlow() {
    this.store.dispatch(goAction({ path: `/flows/${this.flowDetail.etmOrderNumber}/accept` }));
  }

  editDeliveryMethodClicked() {
    this.store.dispatch(new actions.FlowDetailEditDeliveryMethodAction());
    this.sandbox.resetMaxAmountPopup();
  }

  editDetailClicked() {
    this.store.dispatch(new actions.FlowDetailCreateEditFlowDetailAction());
    this.sandbox.resetMaxAmountPopup();
  }

  editPlanningClicked() {
    this.store.dispatch(new actions.FlowDetailCreateEditFlowPlanningAction());
  }

  setCustomer(form): void {
    // if customer is the same, so no new one set => don't load customerInfo again (and clear form)
    if (this.flowDetail.customer && this.flowDetail.customer.id === form.customer.id) {
      this.store.dispatch(new actions.FlowDetailCreateSetCustomerAction({ customer: form.customer }));
    } else {
      this.sandbox
        .loadCustomerInfo(form.customer, true)
        .pipe(first())
        .subscribe(() => {
          this.store.dispatch(new actions.FlowDetailCreateSetCustomerAction({ customer: form.customer }));
        });
    }
  }

  setDeliveryMethod(form): void {
    const orderTypeId = form.type;
    const fromId = form.from.id;
    const toId = form.to.id;
    if (!orderTypeId) {
      this.azureMonitoringService?.logEvent(`FlowEditContainer.setDeliveryMethod(): empty orderTypeId '${form.type}'`);
      return;
    }
    this.sandbox.loadContractInfoMaterials({ orderTypeId, fromId, toId }, !this.flowDetail.handshaker).subscribe({
      next: () => {
        this.store.dispatch(new actions.FlowDetailCreateSetDeliveryMethodAction(form));
        this.triggerPlanningValidation.emit(true);
        this.scrollService.scrollToElement('step-details');
      },
      error: error => {
        this.sandbox.showWarning('ERRORS.CONTRACT-INFO.TITLE', 'ERRORS.CONTRACT-INFO.INVALID_CONTRACT_PART_1');
      }
    });
  }

  removeMaterial(material): void {
    this.store.dispatch(new actions.FlowDetailCreateRemoveMaterialAction(material));
  }

  editMaterial(material) {
    this.store.dispatch(new actions.FlowDetailCreateStartEditMaterialAction({ material }));
  }

  getMaterials(): PackingMaterial[] {
    return this.flowDetail.materials;
  }

  saveMaterial(material: PackingMaterial) {
    if (this.checkMaterialIsUnique(material)) {
      this.store.dispatch(new actions.FlowDetailCreateSaveMaterialAction({ material, isDispute: this.isEditDispute, definitions: this.definitions }));
      this.sandbox.resetMaxAmountPopup();
    }
  }

  async create(data) {
    // Can also be an update
    const isSuccess = await this.sandbox.submitCreateOrUpdate(data);
    if (isSuccess) {
      const editFlow = await getFirst$$(this.store.select(state => state.editFlowDetail));
      this.openCreateFeedbackDialog({
        isSuccess: editFlow.mode.saveSuccess,
        etmOrderNumber: editFlow.flowDetail.etmOrderNumber,
        salesOrderNumber: editFlow.flowDetail.salesOrderNumber,
      });
    }
  }

  downloadDocument(etmOrderNumber: string, extension: string): void {
    const customerIds = (this.customersAllowedToDownload && this.customersAllowedToDownload.map(c => c.id)) || [];
    this.store.dispatch(new actions.FlowDetailDocumentDownloadAction({ etmOrderNumber, extension, customerIds }));
  }

  backToDeliveryClicked() {
    this.store.dispatch(new actions.FlowDetailGoBackToDeliveryAction('flow'));
  }

  backToDetailClicked() {
    this.store.dispatch(new actions.FlowDetailGoBackToDetailAction('flow'));
  }

  saveDetails() {
    this.store.dispatch(new actions.FlowDetailSaveFlowDetailAction());
    this.sandbox.resetMaxAmountPopup();
    this.scrollService.scrollToElement('step-planning');
  }

  savePlanning(form) {
    this.store.dispatch(new actions.FlowDetailSavePlanningAction(form));
    this.scrollService.scrollToElement('submit-button', 200);
  }

  resetMaterialsAndPlanning() {
    this.store.dispatch(new actions.FlowDetailResetMaterialsAndPlanningAction());
  }

  submitClicked() {
    if (this.isEditDispute) {
      this.sandbox.sendDisputeFlow(this.flowDetail);
    } else if (
      this.mode.state === 'edit-confirm' &&
      (this.flowDetail.handshaker || this.flowDetail.lateHandshake) &&
      this.flowDetail.status === appSettings.FLOW_STATUS_WAITING_FOR_HANDSHAKE
    ) {
      this.store.dispatch(new actions.FlowAcceptFlowStartAction());
    } else {
      this.create({}); // can also be an update
    }
  }

  protected openCreateFeedbackDialog({ isSuccess, etmOrderNumber, salesOrderNumber }): Promise<void> {
    let messageType = '';
    if (this.flowDetail.handshaker || this.flowDetail.lateHandshake) {
      messageType = 'HANDSHAKE';
    }
    return super.openCreateFeedbackDialog({
      isSuccess,
      etmOrderNumber,
      salesOrderNumber,
      messageType,
      incoterm: null,
      ccrNumber: null,
      showDownloadDocumentButton: true,
      showCreateButton: true,
      moduleRoute: 'flows',
      createAction: new actions.FlowDetailCreateStartAction({
        selectedCustomer: this.flowDetail.deliveryMethod.soldTo,
        isSuperUser: this.isSuperUser,
      }),
    });
  }

  loadContractInfoFromFlowTypeToIncoterm(flowTypeId: string) {
    this.sandbox.loadContractInfoFromFlowTypeToIncoterm(flowTypeId, !this.flowDetail.handshaker);
  }

  resetCreatePage() {
    this.sandbox.resetCreatePage();
  }

  showEmptyContractModal() {
    this.sandbox.showEmptyContractModal();
  }

  showQuantityModal() {
    this.sandbox.showQuantityExceedsTreshholdModal();
  }

  isCustomerAllowedtoCreateOrUpdate(customerId: string): boolean {
    return !!this.customers.find(c => c.id === customerId);
  }

  sortChanged({ sortField, sortAscending }: { sortField: string, sortAscending: boolean }) {
    this.sandbox.sortMaterials(sortField, sortAscending);
  }

  /**
   * Override cancel to refresh flows list if handshake was performed
   */
  cancel() {
    // If a handshake was successfully performed, refresh the flows list
    // to ensure the handshaken flow is removed from the "waiting for handshake" filter
    if (this.isHandshakeFlow && this.mode.saved && this.mode.saveSuccess) {
      this.store.dispatch(goAction({ path: '/flows/refresh' }));
    } else {
      // Otherwise, use the default behavior
      super.cancel();
    }
  }

  private defineFlowEditContainerStatus() {
    if (this.flowDetail.lateHandshake && this.flowDetail.registrar) {
      this.isLateHandshakeFlow = true;
      return;
    }
    this.route.url.pipe(first()).subscribe(urlParts => {
      switch (urlParts[urlParts.length - 1].path) {
        case 'dispute':
          this.isEditDispute = true;
          break;
        case 'copy':
        case 'new':
          // previous: mode.state === 'create' || mode.state === 'create-confirm'
          this.isCreateFlow = true;
          break;
        case 'edit':
          // previous: (mode.state === 'edit' || mode.state === 'edit-confirm') && flowDetail.registrar && !flowDetail.lateHandshake
          this.isEditFlow = true;
          break;
        case 'accept':
          // previous: (mode.state === 'edit' || mode.state === 'edit-confirm') && (flowDetail.handshaker || flowDetail.lateHandshake)
          this.isHandshakeFlow = true;
          break;
        default:
          this.isReadOnly = true;
          break;
      }
    });
  }
}
